import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Loader from "react-spinner-loader";
import { humanlossbarchartdata, humanlosstrendchartdata } from "../../../../../Services/EndPoints";
import { getData } from "../../../../../Services/Services";
import "../HumanLossDashboard.css";

const BLUES = { incidents: "#3A85B5", deaths: "#1C4E72", injured: "#5AA0C0" };

const FamilyAffectedTrendViewHumanLoss = ({
  title, activeTab, selectedIncident,
  fromDateNew, toDateNew,
  applyClicked, onApplyHandled, firstLoad, setLoader,
}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [toggleChart, setToggleChart] = useState("trendChart");
  const [barData, setBarData] = useState([]);
  const [trendData, setTrendData] = useState(null);

  const fetchData = async () => {
    if (!selectedIncident) return;
    setLoader?.(true);
    try {
      const bar = await getData(humanlossbarchartdata.replace("FROMDATE", fromDateNew).replace("TODATE", toDateNew).replace("ID", selectedIncident.id));
      setBarData(bar || []);
      const trend = await getData(humanlosstrendchartdata.replace("FROMDATE", fromDateNew).replace("TODATE", toDateNew).replace("ID", selectedIncident.id));
      setTrendData(trend || {});
    } catch (e) { console.error(e); }
    finally { setLoader?.(false); }
  };

  useEffect(() => { if (firstLoad) fetchData(); }, [firstLoad]);
  useEffect(() => { if (applyClicked) { fetchData(); onApplyHandled?.(); } }, [applyClicked]);
  useEffect(() => { if (selectedIncident) fetchData(); }, [selectedIncident]);

  // Build bar chart options
  const getBarOptions = () => {
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const sorted = [...barData].sort((a, b) => a.published_month - b.published_month);
    return {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" },
        backgroundColor: "#fff", borderColor: "rgba(42,101,145,.1)", borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);font-family:Inter,sans-serif;" },
      legend: { data: ["Incidents", "Deaths", "Injured"], bottom: 4, textStyle: { color: "#3A5F78", fontSize: 10, fontWeight: 600 }, itemWidth: 10, itemHeight: 10 },
      grid: { left: 12, right: 12, top: 20, bottom: 48, containLabel: true },
      xAxis: { type: "category", data: sorted.map(d => months[d.published_month - 1] || ""), axisLine: { lineStyle: { color: "rgba(42,101,145,.1)" } }, axisTick: { show: false }, axisLabel: { color: "#3A5F78", fontSize: 10, fontWeight: 600 } },
      yAxis: { type: "value", splitLine: { lineStyle: { color: "rgba(42,101,145,.07)", type: "dashed" } }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: "#7A9BB0", fontSize: 10 } },
      series: [
        { name: "Incidents", type: "bar", data: sorted.map(d => d.incident_count), itemStyle: { color: BLUES.incidents, borderRadius: [6,6,0,0] }, barGap: "10%", label: { show: true, position: "top", fontSize: 10, fontWeight: 700, color: "#3A5F78", formatter: p => p.value > 0 ? p.value : "" } },
        { name: "Deaths", type: "bar", data: sorted.map(d => d.deaths_count), itemStyle: { color: BLUES.deaths, borderRadius: [6,6,0,0] }, barGap: "10%", label: { show: true, position: "top", fontSize: 10, fontWeight: 700, color: "#1C4E72", formatter: p => p.value > 0 ? p.value : "" } },
        { name: "Injured", type: "bar", data: sorted.map(d => d.injured_count), itemStyle: { color: BLUES.injured, borderRadius: [6,6,0,0] }, barGap: "10%", label: { show: true, position: "top", fontSize: 10, fontWeight: 700, color: "#5AA0C0", formatter: p => p.value > 0 ? p.value : "" } },
      ],
    };
  };

  // Build trend chart options
  const getTrendOptions = () => {
    if (!trendData) return {};
    const allDates = [];
    ["injured","death","incident"].forEach(k => { if (trendData[k]) allDates.push(...trendData[k].map(i => moment(i.reported_on).format("DD-MM-YY"))); });
    const dateArr = [...new Set(allDates)].sort((a, b) => moment(a, "DD-MM-YY").diff(moment(b, "DD-MM-YY")));
    const series = { incident: new Array(dateArr.length).fill(0), death: new Array(dateArr.length).fill(0), injured: new Array(dateArr.length).fill(0) };
    trendData.incident?.forEach(i => { const idx = dateArr.indexOf(moment(i.reported_on).format("DD-MM-YY")); if (idx >= 0) series.incident[idx] = i.incident_count || 0; });
    trendData.death?.forEach(i => { const idx = dateArr.indexOf(moment(i.reported_on).format("DD-MM-YY")); if (idx >= 0) series.death[idx] = i.deaths_count || 0; });
    trendData.injured?.forEach(i => { const idx = dateArr.indexOf(moment(i.reported_on).format("DD-MM-YY")); if (idx >= 0) series.injured[idx] = i.injured_count || 0; });

    return {
      tooltip: { trigger: "axis", backgroundColor: "#fff", borderColor: "rgba(42,101,145,.1)", borderWidth: 1, extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);" },
      legend: { data: ["Incidents", "Deaths", "Injured"], bottom: 4, textStyle: { color: "#3A5F78", fontSize: 10, fontWeight: 600 }, itemWidth: 10, itemHeight: 10 },
      grid: { left: 12, right: 12, top: 20, bottom: 48, containLabel: true },
      xAxis: { type: "category", data: dateArr, axisLabel: { rotate: 45, color: "#7A9BB0", fontSize: 9 }, axisLine: { lineStyle: { color: "rgba(42,101,145,.1)" } } },
      yAxis: { type: "value", splitLine: { lineStyle: { color: "rgba(42,101,145,.07)", type: "dashed" } }, axisLabel: { color: "#7A9BB0", fontSize: 10 }, axisLine: { show: false } },
      dataZoom: [{ type: "slider", height: 16, bottom: 28, borderColor: "transparent", backgroundColor: "rgba(42,101,145,.04)", fillerColor: "rgba(42,101,145,.12)", handleStyle: { color: "#2A6591" } }],
      series: [
        { name: "Incidents", type: "line", data: series.incident, smooth: 0.4, symbolSize: 7, showSymbol: false, lineStyle: { width: 2.5, color: BLUES.incidents }, itemStyle: { color: BLUES.incidents }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: BLUES.incidents + "99" }, { offset: 0.7, color: BLUES.incidents + "33" }, { offset: 1, color: BLUES.incidents + "08" }]) }, emphasis: { focus: "series" } },
        { name: "Deaths", type: "line", data: series.death, smooth: 0.4, symbolSize: 7, showSymbol: false, lineStyle: { width: 2.5, color: BLUES.deaths }, itemStyle: { color: BLUES.deaths }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: BLUES.deaths + "99" }, { offset: 0.7, color: BLUES.deaths + "33" }, { offset: 1, color: BLUES.deaths + "08" }]) }, emphasis: { focus: "series" } },
        { name: "Injured", type: "line", data: series.injured, smooth: 0.4, symbolSize: 7, showSymbol: false, lineStyle: { width: 2.5, color: BLUES.injured }, itemStyle: { color: BLUES.injured }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: BLUES.injured + "99" }, { offset: 0.7, color: BLUES.injured + "33" }, { offset: 1, color: BLUES.injured + "08" }]) }, emphasis: { focus: "series" } },
      ],
    };
  };

  useEffect(() => {
    if (!chartRef.current) return;
    if (chartInstance.current) chartInstance.current.dispose();
    chartInstance.current = echarts.init(chartRef.current);
    const opt = toggleChart === "bar" && barData.length > 0 ? getBarOptions() : toggleChart === "trendChart" && trendData ? getTrendOptions() : null;
    if (opt) chartInstance.current.setOption(opt); else chartInstance.current.clear();
    const resize = () => chartInstance.current?.resize();
    window.addEventListener("resize", resize);
    return () => { window.removeEventListener("resize", resize); chartInstance.current?.dispose(); };
  }, [toggleChart, barData, trendData, selectedIncident]);

  if (!selectedIncident) return (
    <div className="hld-panel"><div className="hld-empty"><div className="hld-empty-icon">
      <svg viewBox="0 0 48 48" fill="none" width="36" height="36"><rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/><rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/><rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/></svg>
    </div><p className="hld-empty-text">Click on an incident card to view trend data</p></div></div>
  );

  if (selectedIncident.id === 0 || selectedIncident.id === "ALL") return (
    <div className="hld-panel"><div className="hld-empty"><p className="hld-empty-text">No trend data available</p></div></div>
  );

  return (
    <div className="hld-panel">
      <div className="hld-panel-header">
        <div>
          <div className="hld-panel-title">{title || "Chart"}</div>
          <div className="hld-panel-sub">Monthwise & daily trend</div>
        </div>
        <div className="hld-toggle">
          <div className={`hld-toggle-item${toggleChart === "trendChart" ? " hld-toggle-item--active" : ""}`} onClick={() => setToggleChart("trendChart")}>Chart</div>
          <div className={`hld-toggle-item${toggleChart === "bar" ? " hld-toggle-item--active" : ""}`} onClick={() => setToggleChart("bar")}>Bar</div>
        </div>
      </div>
      <div className="hld-panel-body">
        <div ref={chartRef} style={{ width: "100%", height: "100%", minHeight: 320 }} />
      </div>
    </div>
  );
};

export default FamilyAffectedTrendViewHumanLoss;